import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment as env } from '@macs-frontend/env';
import { State } from '@macs-frontend/reducers';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { SetToken } from '../actions/auth.actions';
import { LoginPayload } from '../models/login.model';
import { getToken } from '../reducers';
import { KeycloakService } from './keycloak.service';

export interface Token {
  username: string;
  iat: number;
  exp: number;
  company?: string;
  company_roles?: Record<string, string[]>;
  preferred_username?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {
  constructor(
    httpClient: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private keycloak: KeycloakService,
    store: Store<State>,
  ) {
    super(httpClient);
    store
      .select(getToken)
      .pipe(take(1))
      .subscribe(token => {
        const actualToken = this.getToken();
        if (token !== actualToken) {
          store.dispatch(new SetToken(actualToken));
        }
      });
  }

  getToken(): string {
    if (this.keycloak.enabled && this.keycloak.token) {
      return this.keycloak.token;
    }
    return localStorage.getItem('token') ?? '';
  }

  decodeToken(): Token | null {
    if (this.keycloak.enabled && this.keycloak.tokenParsed) {
      const p = this.keycloak.tokenParsed;
      return {
        username: (p['preferred_username'] ?? p['sub']) as string,
        iat: (p['iat'] as number) ?? 0,
        exp: (p['exp'] as number) ?? 0,
        company: (p['company'] ?? p['companyUuid']) as string | undefined,
        company_roles: p['company_roles'] as Token['company_roles'],
        preferred_username: p['preferred_username'] as string | undefined,
      };
    }
    const t = localStorage.getItem('token');
    return t ? this.jwtHelper.decodeToken(t) : null;
  }

  get company(): string | undefined {
    if (this.keycloak.enabled) {
      const cr = this.keycloak.companyRoles;
      if (cr) {
        const keys = Object.keys(cr);
        if (keys.length > 0) return keys[0];
      }
      return this.keycloak.company;
    }
    return (this.decodeToken() as Token)?.company;
  }

  login(reqPayload: LoginPayload) {
    if (this.keycloak.enabled) {
      this.keycloak.login();
      return EMPTY;
    }
    return this.http.post(`${env.api_url}/login`, {
      username: reqPayload.username, // 'user@user.com',
      password: reqPayload.password, // 'useruser'
    });
  }

  logout() {
    if (this.keycloak.enabled) {
      this.keycloak.logout();
      return;
    }
    localStorage.removeItem('token');
    this.router.navigate(['/auth/login']);
  }

  isAuthenticated(): boolean {
    if (this.keycloak.enabled) {
      return this.keycloak.authenticated;
    }
    const token = this.getToken();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  sendEmail(email: string) {
    return this.http.patch(
      `${this.API_PATH}/forgot_password/send_email?email=${encodeURIComponent(email)}`,
      null,
    );
  }

  resetPassword(payload: { username: string; password: string; token: string }) {
    return this.http.patch(`${this.API_PATH}/forgot_password/change_password`, payload);
  }

  validateToken(token: string, username: string) {
    return this.http.patch(
      `${this.API_PATH}/forgot_password/check_token?token=${encodeURIComponent(
        token,
      )}&username=${encodeURIComponent(username)}`,
      {},
    );
  }
}
